<template>
  <div v-if="assignedNumbers.length > 0" class="relative" data-phone-status>
    <!-- Phone button with status text and voicemail badge -->
    <button
      type="button"
      class="relative flex items-center gap-1.5 px-2 py-1 rounded-md text-sm text-neutral-900 transition-colors"
      :class="dropdownOpen ? 'bg-white shadow-sm' : 'hover:bg-white'"
      :title="statusTitle"
      @click="toggleDropdown"
    >
      <!-- Unread voicemail dot -->
      <span
        v-if="unreadVoicemails > 0"
        class="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 border border-white pointer-events-none"
      />
      <font-awesome-icon :icon="['fas', 'phone']" class="text-xs" />
      <span class="max-md:hidden text-xs font-medium" :class="statusLabelClass">{{ statusLabel }}</span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="max-md:hidden text-[8px] ml-0.5 transition-transform" :class="{ 'rotate-180': dropdownOpen }" />
    </button>

    <!-- Dropdown panel -->
    <div
      v-if="dropdownOpen"
      class="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
      @click.stop
    >
      <!-- Header -->
      <div class="px-4 py-3 border-b border-gray-100">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <span class="text-sm font-semibold text-gray-900">Phone</span>
          </div>
          <span class="shrink-0 text-xs px-2 py-0.5 rounded-full" :class="statusBadgeClass">
            {{ statusLabel }}
          </span>
        </div>
        <div class="mt-2 space-y-1">
          <p
            v-for="num in assignedNumbers"
            :key="num.id"
            class="flex items-baseline gap-1.5 text-xs text-gray-500"
          >
            <span class="shrink-0 font-medium tabular-nums sensitive">{{ formatPhone(num.phone_number) }}</span>
            <span v-if="num.friendly_name" class="truncate text-gray-400">
              {{ num.friendly_name }}
            </span>
          </p>
        </div>
      </div>

      <!-- Voicemail setup required -->
      <div v-if="needsVoicemailSetup" class="p-4">
        <div class="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="text-amber-500 mt-0.5" />
          <div>
            <p class="text-sm font-medium text-amber-800">Voicemail not configured</p>
            <p class="text-xs text-amber-700 mt-1">
              Set up a voicemail greeting before going available for calls.
            </p>
            <button
              class="mt-2 text-xs font-semibold text-primary-500 hover:text-primary-600"
              @click="goToPhoneSettings"
            >
              Set up voicemail &rarr;
            </button>
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div v-else class="p-4 space-y-4">
        <!-- Availability toggle -->
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm font-medium text-gray-900">Available for calls</p>
            <p class="text-xs text-gray-500">
              {{ available ? 'You will receive inbound calls' : 'Calls go to voicemail' }}
            </p>
          </div>
          <button
            class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
            :class="available ? 'bg-green-500' : 'bg-gray-300'"
            :disabled="toggling"
            @click="toggleAvailability"
          >
            <span
              class="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
              :class="available ? 'translate-x-4' : 'translate-x-0.5'"
            />
          </button>
        </div>

        <!-- Phone Inbox link -->
        <button
          class="w-full flex items-center justify-between p-2 -mx-2 rounded-md hover:bg-gray-50 transition-colors"
          @click="goToPhoneInbox"
        >
          <span class="text-sm text-gray-700">{{ threeWayEnabled ? 'Phone Inbox' : 'Voicemail Inbox' }}</span>
          <span
            v-if="unreadVoicemails > 0"
            class="text-xs font-semibold text-primary-500"
          >
            {{ unreadVoicemails }} unread
          </span>
          <span v-else class="text-xs text-gray-400">
            {{ threeWayEnabled ? 'View calls &amp; voicemails' : 'No new messages' }}
          </span>
        </button>

        <!-- Selected audio devices -->
        <div v-if="audioDevicesLoaded" class="space-y-2 text-xs text-gray-500">
          <div class="flex items-center gap-2">
            <font-awesome-icon :icon="['fas', 'microphone']" class="w-3 text-gray-400" />
            <span class="truncate">{{ selectedMicLabel }}</span>
          </div>
          <div class="flex items-center gap-2">
            <font-awesome-icon :icon="['fas', 'volume-high']" class="w-3 text-gray-400" />
            <span class="truncate">{{ selectedSpeakerLabel }}</span>
          </div>
        </div>

        <!-- Phone settings link -->
        <div class="pt-2 border-t border-gray-100">
          <button
            class="text-xs text-gray-500 hover:text-primary-500"
            @click="goToPhoneSettings"
          >
            <font-awesome-icon :icon="['fas', 'gear']" class="mr-1" />
            Phone settings
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { twilioDevice } from '@/services/TwilioDeviceService'
import { listAvailableNumbers } from '@/services/api/tenantPhoneNumbers'
import { isPostHogFlagEnabled } from '@/utils/posthogFlags'
import { POSTHOG_FLAGS } from '@/constants/posthogFlags'

const PREFS_KEY = 'telehealth_device_prefs'

function cleanDeviceLabel(label) {
  return label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '').trim()
}

export default {
  name: 'PhoneStatusIndicator',
  components: { FontAwesomeIcon },
  data() {
    return {
      dropdownOpen: false,
      phoneStatus: twilioDevice.status,
      unreadVoicemails: twilioDevice.unreadVoicemails,
      toggling: false,
      assignedNumbers: [],
      micDevices: [],
      speakerDevices: [],
      selectedMicId: '',
      selectedSpeakerId: '',
      audioDevicesLoaded: false,
      ringing: false,
      crossTabCallActive: false,
    }
  },
  computed: {
    threeWayEnabled() {
      return isPostHogFlagEnabled(POSTHOG_FLAGS.TELEHEALTH_THREE_WAY_CALLING)
    },
    needsVoicemailSetup() {
      return (
        this.assignedNumbers.length > 0 &&
        this.assignedNumbers.some((number) => !number.greeting_audio)
      )
    },
    available() {
      return this.phoneStatus === 'available'
    },
    onCall() {
      return this.phoneStatus === 'busy'
    },
    statusTitle() {
      if (this.onCall) return 'Busy — on a call'
      if (this.available) return 'Available for calls'
      return 'Offline'
    },
    statusLabel() {
      if (this.onCall) return 'Busy'
      if (this.available) return 'Available'
      if (this.needsVoicemailSetup) return 'Setup Required'
      return 'Offline'
    },
    statusBadgeClass() {
      if (this.onCall) return 'bg-red-100 text-red-700'
      if (this.available) return 'bg-green-100 text-green-700'
      if (this.needsVoicemailSetup) return 'bg-amber-100 text-amber-700'
      return 'bg-gray-100 text-gray-600'
    },
    statusLabelClass() {
      if (this.onCall) return 'text-red-600'
      if (this.available) return 'text-green-700'
      if (this.needsVoicemailSetup) return 'text-amber-700'
      return 'text-neutral-800'
    },
    selectedMicLabel() {
      const d = this.micDevices.find((d) => d.deviceId === this.selectedMicId)
      return d ? cleanDeviceLabel(d.label) : 'Default microphone'
    },
    selectedSpeakerLabel() {
      const d = this.speakerDevices.find((d) => d.deviceId === this.selectedSpeakerId)
      return d ? cleanDeviceLabel(d.label) : 'Default speaker'
    },
  },
  watch: {
    $route() {
      this._fetchAssignedNumber()
    },
  },
  mounted() {
    this._fetchAssignedNumber()
    this._settingTitle = false
    this._titleObserver = new MutationObserver(() => {
      if (this._settingTitle) return
      const raw = document.title.replace(/^[\u{1F4DE}\u{1F7E2}]\s/u, '')
      this._originalTitle = raw
      this._updateTabTitle(this.phoneStatus)
    })
    const titleEl = document.querySelector('title')
    if (titleEl) {
      this._titleObserver.observe(titleEl, { childList: true })
    }
    this._originalTitle = document.title
    this._handleClickOutside = (e) => {
      if (this.dropdownOpen && !e.target.closest('[data-phone-status]')) {
        this.dropdownOpen = false
      }
    }
    document.addEventListener('click', this._handleClickOutside)
    window.addEventListener('phone-settings-changed', this._onSettingsChanged)
    this._onIncoming = () => { this.ringing = true; this._updateTabTitle(this.phoneStatus) }
    this._onCallConnected = () => { this.ringing = false; this._syncStatus() }
    this._onCallDisconnected = () => { this.ringing = false; this.crossTabCallActive = false; this._syncStatus() }
    this._onCrossTabCall = (ctx) => {
      this.ringing = false
      this.crossTabCallActive = !!ctx
      this._updateTabTitle(this.phoneStatus)
    }
    twilioDevice.on('statusChanged', this._onStatusChanged)
    twilioDevice.on('unreadVoicemailsChanged', this._onUnreadChanged)
    twilioDevice.on('incoming', this._onIncoming)
    twilioDevice.on('callConnected', this._onCallConnected)
    twilioDevice.on('callDisconnected', this._onCallDisconnected)
    twilioDevice.on('crossTabCall', this._onCrossTabCall)
    this._updateTabTitle(this.phoneStatus)
  },
  beforeUnmount() {
    if (this._titleObserver) this._titleObserver.disconnect()
    document.title = this._originalTitle
    document.removeEventListener('click', this._handleClickOutside)
    window.removeEventListener('phone-settings-changed', this._onSettingsChanged)
    twilioDevice.off('statusChanged', this._onStatusChanged)
    twilioDevice.off('unreadVoicemailsChanged', this._onUnreadChanged)
    twilioDevice.off('incoming', this._onIncoming)
    twilioDevice.off('callConnected', this._onCallConnected)
    twilioDevice.off('callDisconnected', this._onCallDisconnected)
    twilioDevice.off('crossTabCall', this._onCrossTabCall)
    this._cleanupAudio()
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
      if (this.dropdownOpen) {
        this._fetchAssignedNumber()
        if (!this.audioDevicesLoaded) {
          this._enumerateDevices()
        }
      }
    },
    async toggleAvailability() {
      this.toggling = true
      try {
        if (this.available) {
          await twilioDevice.unregister()
        } else {
          if (!twilioDevice.isInitialized) {
            await twilioDevice.initialize()
          }
          await twilioDevice.register()
        }
      } catch (e) {
        console.error('[PhoneStatus] toggle failed:', e)
      } finally {
        this.toggling = false
      }
    },
    async _fetchAssignedNumber() {
      try {
        const numbers = await listAvailableNumbers()
        const userId = this.$store.getters['auth/current_user']?.id
        this.assignedNumbers = numbers.filter((n) => n.assigned_user === userId)
        if (this.assignedNumbers.length > 0 && !twilioDevice.isInitialized) {
          await twilioDevice.initialize()
        }
        if (this.available && this.needsVoicemailSetup) {
          await twilioDevice.unregister()
        }
      } catch (e) {
        console.error('[PhoneStatus] _fetchAssignedNumber failed:', e)
      }
    },
    formatPhone(number) {
      if (!number) return ''
      const digits = number.replace(/\D/g, '')
      if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      return number
    },
    async _enumerateDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        this.micDevices = devices.filter((d) => d.kind === 'audioinput')
        this.speakerDevices = devices.filter((d) => d.kind === 'audiooutput')

        const saved = this._loadPrefs()
        this.selectedMicId =
          (saved.audioDeviceId &&
            this.micDevices.find((d) => d.deviceId === saved.audioDeviceId)
              ?.deviceId) ||
          this.micDevices[0]?.deviceId ||
          ''
        this.selectedSpeakerId =
          (saved.audioOutputDeviceId &&
            this.speakerDevices.find(
              (d) => d.deviceId === saved.audioOutputDeviceId,
            )?.deviceId) ||
          this.speakerDevices[0]?.deviceId ||
          ''

        this.audioDevicesLoaded = true

        navigator.mediaDevices.addEventListener(
          'devicechange',
          this._onDeviceChange,
        )
      } catch (e) {
        console.error('[PhoneStatus] device enumeration failed:', e)
      }
    },
    _onDeviceChange() {
      this._enumerateDevices()
    },
    switchMic(deviceId) {
      this.selectedMicId = deviceId
      this._savePrefs()
      this._applyAudioDevices()
    },
    switchSpeaker(deviceId) {
      this.selectedSpeakerId = deviceId
      this._savePrefs()
      this._applyAudioDevices()
    },
    async _applyAudioDevices() {
      if (!twilioDevice.device) return
      try {
        if (this.selectedMicId) {
          await twilioDevice.device.audio.setInputDevice(this.selectedMicId)
        }
        if (this.selectedSpeakerId) {
          twilioDevice.device.audio.speakerDevices.set(this.selectedSpeakerId)
        }
      } catch (e) {
        console.error('[PhoneStatus] failed to apply audio devices:', e)
      }
    },
    _loadPrefs() {
      try {
        return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}')
      } catch {
        return {}
      }
    },
    _savePrefs() {
      try {
        const existing = this._loadPrefs()
        localStorage.setItem(
          PREFS_KEY,
          JSON.stringify({
            ...existing,
            audioDeviceId: this.selectedMicId,
            audioOutputDeviceId: this.selectedSpeakerId,
          }),
        )
      } catch {
        // storage unavailable
      }
    },
    goToPhoneSettings() {
      this.dropdownOpen = false
      this.$router.push('/phone-settings')
    },
    goToPhoneInbox() {
      this.dropdownOpen = false
      const route = this.threeWayEnabled && this.unreadVoicemails === 0 ? 'PhoneInboxCalls' : 'PhoneInboxVoicemails'
      this.$router.push({ name: route })
    },
    _onSettingsChanged() {
      this._fetchAssignedNumber()
    },
    _onStatusChanged(status) {
      this.phoneStatus = status
      this._updateTabTitle(status)
    },
    _syncStatus() {
      this.phoneStatus = twilioDevice.status
      this._updateTabTitle(this.phoneStatus)
    },
    _updateTabTitle(status) {
      const base = this._originalTitle || 'Kairon Health'
      let title
      if (this.ringing) {
        title = '\u{1F4DE} ' + base
      } else if (twilioDevice.activeCall) {
        title = '\u{1F4DE} ' + base
      } else if (this.crossTabCallActive) {
        title = base
      } else if (status === 'available') {
        title = '\u{1F7E2} ' + base
      } else {
        title = base
      }
      if (document.title !== title) {
        this._settingTitle = true
        document.title = title
        this._settingTitle = false
      }
    },
    _onUnreadChanged(count) {
      this.unreadVoicemails = count
    },
    _cleanupAudio() {
      try {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          this._onDeviceChange,
        )
      } catch {
        // ignore
      }
    },
  },
}
</script>
